import { useState, useCallback, useMemo } from 'react';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { useDronesLegacyHoursQuery, usePilotsLegacyHoursQuery } from '../adminTools.queries';
import {
  useUpdateDroneLegacyHoursMutation,
  useUpdatePilotLegacyHoursMutation,
} from '../adminTools.mutations';
import type { DroneLegacyHoursItem, PilotLegacyHoursItem } from '../types/adminTools.types';

export interface LegacyHoursFormState {
  pre_2023: string;
  year_2023: string;
  year_2024: string;
}

export interface LegacyHoursValidationState {
  pre_2023: boolean;
  year_2023: boolean;
  year_2024: boolean;
}

export interface UseLegacyHoursMigrationReturn {
  // Dialog state
  dialogOpen: boolean;
  loading: boolean;
  saving: boolean;

  // Tab state
  activeTab: 'drones' | 'pilots';

  // Data
  dronesData: DroneLegacyHoursItem[];
  pilotsData: PilotLegacyHoursItem[];

  // Selection state
  selectedDrone: string;
  selectedPilot: string;

  // Form state
  formState: LegacyHoursFormState;
  validationErrors: LegacyHoursValidationState;
  hasValidationErrors: boolean;

  // Actions
  openDialog: () => Promise<void>;
  closeDialog: () => void;
  handleTabChange: (tab: 'drones' | 'pilots') => void;
  selectDrone: (serialNumber: string) => void;
  selectPilot: (userId: string) => void;
  saveDroneLegacyHours: () => Promise<void>;
  savePilotLegacyHours: () => Promise<void>;

  // Form setters
  setPre2023: (value: string) => void;
  setYear2023: (value: string) => void;
  setYear2024: (value: string) => void;
}

// Check if a time string is valid (empty is valid, non-empty must be parseable)
function isValidTimeString(timeStr: string): boolean {
  if (!timeStr || timeStr.trim() === '') return true; // Empty is valid (means no value)
  const parts = timeStr.split(':');
  if (parts.length === 3) {
    // HH:MM:SS format - check all parts are numeric
    return parts.every((p) => /^\d+$/.test(p));
  }
  if (parts.length === 2) {
    // HH:MM format - check all parts are numeric
    return parts.every((p) => /^\d+$/.test(p));
  }
  // Try parsing as decimal hours
  return !isNaN(parseFloat(timeStr));
}

// Helper to convert HH:MM:SS or HH:MM to decimal hours
function timeStringToDecimalHours(timeStr: string): number | undefined {
  if (!timeStr || timeStr.trim() === '') return undefined;
  const parts = timeStr.split(':');
  if (parts.length === 3) {
    // HH:MM:SS format
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    const seconds = parseInt(parts[2], 10) || 0;
    return hours + minutes / 60 + seconds / 3600;
  }
  if (parts.length === 2) {
    // HH:MM format
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    return hours + minutes / 60;
  }
  // Try parsing as decimal hours
  const parsed = parseFloat(timeStr);
  return isNaN(parsed) ? undefined : parsed;
}

// Helper to convert decimal hours to HH:MM:SS
function decimalHoursToTimeString(hours: number | undefined): string {
  if (hours === undefined || hours === null) return '';
  const totalSeconds = Math.round(hours * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s
    .toString()
    .padStart(2, '0')}`;
}

const INITIAL_FORM_STATE: LegacyHoursFormState = {
  pre_2023: '',
  year_2023: '',
  year_2024: '',
};

const INITIAL_VALIDATION_STATE: LegacyHoursValidationState = {
  pre_2023: false,
  year_2023: false,
  year_2024: false,
};

export function useLegacyHoursMigration(): UseLegacyHoursMigrationReturn {
  const { showError, showSuccess } = useErrorHandler();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<'drones' | 'pilots'>('drones');

  // Server data — fetched only while the dialog is open.
  const dronesQuery = useDronesLegacyHoursQuery(dialogOpen);
  const pilotsQuery = usePilotsLegacyHoursQuery(dialogOpen);
  // Memoized so the fallback empty array is stable across renders (these feed
  // useCallback dependency arrays below).
  const dronesData = useMemo(() => dronesQuery.data?.drones ?? [], [dronesQuery.data]);
  const pilotsData = useMemo(() => pilotsQuery.data?.pilots ?? [], [pilotsQuery.data]);
  const loading = dialogOpen && (dronesQuery.isLoading || pilotsQuery.isLoading);

  const updateDroneMutation = useUpdateDroneLegacyHoursMutation();
  const updatePilotMutation = useUpdatePilotLegacyHoursMutation();
  const saving = updateDroneMutation.isPending || updatePilotMutation.isPending;

  // Selection state
  const [selectedDrone, setSelectedDrone] = useState<string>('');
  const [selectedPilot, setSelectedPilot] = useState<string>('');

  // Form state
  const [formState, setFormState] = useState<LegacyHoursFormState>(INITIAL_FORM_STATE);
  const [validationErrors, setValidationErrors] =
    useState<LegacyHoursValidationState>(INITIAL_VALIDATION_STATE);

  const hasValidationErrors =
    validationErrors.pre_2023 || validationErrors.year_2023 || validationErrors.year_2024;

  const openDialog = useCallback(async () => {
    setDialogOpen(true);
    setActiveTab('drones');
    setSelectedDrone('');
    setSelectedPilot('');
    setFormState(INITIAL_FORM_STATE);
    setValidationErrors(INITIAL_VALIDATION_STATE);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedDrone('');
    setSelectedPilot('');
    setFormState(INITIAL_FORM_STATE);
    setValidationErrors(INITIAL_VALIDATION_STATE);
  }, []);

  const handleTabChange = useCallback((tab: 'drones' | 'pilots') => {
    setActiveTab(tab);
    setSelectedDrone('');
    setSelectedPilot('');
    setFormState(INITIAL_FORM_STATE);
    setValidationErrors(INITIAL_VALIDATION_STATE);
  }, []);

  const selectDrone = useCallback(
    (serialNumber: string) => {
      setSelectedDrone(serialNumber);
      setValidationErrors(INITIAL_VALIDATION_STATE);
      const drone = dronesData.find((d) => d.serial_number === serialNumber);
      if (drone?.legacy_hours) {
        setFormState({
          pre_2023: decimalHoursToTimeString(drone.legacy_hours.pre_2023),
          year_2023: decimalHoursToTimeString(drone.legacy_hours['2023']),
          year_2024: decimalHoursToTimeString(drone.legacy_hours['2024']),
        });
      } else {
        setFormState(INITIAL_FORM_STATE);
      }
    },
    [dronesData]
  );

  const selectPilot = useCallback(
    (userId: string) => {
      setSelectedPilot(userId);
      setValidationErrors(INITIAL_VALIDATION_STATE);
      const pilot = pilotsData.find((p) => p.user_id === userId);
      if (pilot?.legacy_flight_hours) {
        setFormState({
          pre_2023: decimalHoursToTimeString(pilot.legacy_flight_hours.pre_2023),
          year_2023: decimalHoursToTimeString(pilot.legacy_flight_hours['2023']),
          year_2024: decimalHoursToTimeString(pilot.legacy_flight_hours['2024']),
        });
      } else {
        setFormState(INITIAL_FORM_STATE);
      }
    },
    [pilotsData]
  );

  const saveDroneLegacyHours = useCallback(async () => {
    if (!selectedDrone) return;

    try {
      const hours = {
        pre_2023: timeStringToDecimalHours(formState.pre_2023),
        '2023': timeStringToDecimalHours(formState.year_2023),
        '2024': timeStringToDecimalHours(formState.year_2024),
      };

      // Mutation invalidates the drones-legacy-hours query, refreshing the list.
      await updateDroneMutation.mutateAsync({ serialNumber: selectedDrone, hours });
      showSuccess(`Legacy hours saved for drone ${selectedDrone}`);
    } catch (error) {
      showError(error, 'Failed to save legacy hours');
    }
  }, [selectedDrone, formState, updateDroneMutation, showError, showSuccess]);

  const savePilotLegacyHours = useCallback(async () => {
    if (!selectedPilot) return;

    try {
      const hours = {
        pre_2023: timeStringToDecimalHours(formState.pre_2023),
        '2023': timeStringToDecimalHours(formState.year_2023),
        '2024': timeStringToDecimalHours(formState.year_2024),
      };

      // Mutation invalidates the pilots-legacy-hours query, refreshing the list.
      await updatePilotMutation.mutateAsync({ userId: selectedPilot, hours });
      const pilot = pilotsData.find((p) => p.user_id === selectedPilot);
      showSuccess(`Legacy hours saved for ${pilot?.full_name || pilot?.email}`);
    } catch (error) {
      showError(error, 'Failed to save legacy hours');
    }
  }, [selectedPilot, formState, pilotsData, updatePilotMutation, showError, showSuccess]);

  const setPre2023 = useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, pre_2023: value }));
    setValidationErrors((prev) => ({ ...prev, pre_2023: !isValidTimeString(value) }));
  }, []);

  const setYear2023 = useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, year_2023: value }));
    setValidationErrors((prev) => ({ ...prev, year_2023: !isValidTimeString(value) }));
  }, []);

  const setYear2024 = useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, year_2024: value }));
    setValidationErrors((prev) => ({ ...prev, year_2024: !isValidTimeString(value) }));
  }, []);

  return {
    dialogOpen,
    loading,
    saving,
    activeTab,
    dronesData,
    pilotsData,
    selectedDrone,
    selectedPilot,
    formState,
    validationErrors,
    hasValidationErrors,
    openDialog,
    closeDialog,
    handleTabChange,
    selectDrone,
    selectPilot,
    saveDroneLegacyHours,
    savePilotLegacyHours,
    setPre2023,
    setYear2023,
    setYear2024,
  };
}
